import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

/**
 * Customizable site credit shown across UI surfaces, CSV/JSON metadata, and PDF footers.
 * Persisted in localStorage so the choice survives reloads and is read synchronously
 * by export utilities via the helper functions exported below.
 */
export interface SiteCredit {
  name: string;
  url: string;
}

export const DEFAULT_SITE_CREDIT: SiteCredit = {
  name: 'EMFJewellers',
  url: 'http://emfjewellers.com',
};

const STORAGE_KEY = 'mgl_site_credit_v1';

function readStored(): SiteCredit {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SITE_CREDIT;
    const parsed = JSON.parse(raw);
    const name = typeof parsed?.name === 'string' && parsed.name.trim() ? parsed.name.trim() : DEFAULT_SITE_CREDIT.name;
    const url = typeof parsed?.url === 'string' && parsed.url.trim() ? parsed.url.trim() : DEFAULT_SITE_CREDIT.url;
    return { name, url };
  } catch {
    return DEFAULT_SITE_CREDIT;
  }
}

/**
 * Synchronous accessor for non-React modules (PDF/CSV builders).
 * Always returns sensible defaults if storage is empty or corrupted.
 */
export function getSiteCredit(): SiteCredit {
  if (typeof window === 'undefined') return DEFAULT_SITE_CREDIT;
  return readStored();
}

/** Strip protocol for display-friendly footer text (e.g. "emfjewellers.com"). */
export function getCreditHost(credit: SiteCredit = getSiteCredit()): string {
  try {
    return new URL(credit.url).host || credit.url;
  } catch {
    return credit.url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }
}

interface Ctx {
  credit: SiteCredit;
  setCredit: (next: SiteCredit) => void;
  reset: () => void;
}

const SiteCreditContext = createContext<Ctx | null>(null);

export function SiteCreditProvider({ children }: { children: ReactNode }) {
  const [credit, setCreditState] = useState<SiteCredit>(() => readStored());

  // Cross-tab sync.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setCreditState(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setCredit = useCallback((next: SiteCredit) => {
    const cleaned: SiteCredit = {
      name: next.name.trim() || DEFAULT_SITE_CREDIT.name,
      url: next.url.trim() || DEFAULT_SITE_CREDIT.url,
    };
    setCreditState(cleaned);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned)); } catch { /* quota */ }
  }, []);

  const reset = useCallback(() => {
    setCreditState(DEFAULT_SITE_CREDIT);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
  }, []);

  const value = useMemo(() => ({ credit, setCredit, reset }), [credit, setCredit, reset]);

  return <SiteCreditContext.Provider value={value}>{children}</SiteCreditContext.Provider>;
}

export function useSiteCredit(): Ctx {
  const ctx = useContext(SiteCreditContext);
  if (!ctx) throw new Error('useSiteCredit must be used within SiteCreditProvider');
  return ctx;
}
